import { memo } from 'react'
import { useHasPendingRowUpdate } from '@/hooks/usePendingRowUpdate'
import { cn } from '@/lib/utils'

interface PendingUpdateIndicatorProps {
  rowId: string
  className?: string
}

/**
 * Visual indicator showing that a row has pending real-time updates
 * that will be applied when the user closes their current popover.
 *
 * Shows a subtle pulsing dot in the corner of the row.
 */
export const PendingUpdateIndicator = memo(function PendingUpdateIndicator({
  rowId,
  className,
}: PendingUpdateIndicatorProps) {
  const hasPendingUpdate = useHasPendingRowUpdate(rowId)

  if (!hasPendingUpdate) {
    return null
  }

  return (
    <td className="relative w-0 border-0 p-0" aria-hidden="true">
      <div
        className={cn(
          'absolute right-1 top-1/2 z-10 -translate-y-1/2',
          'flex items-center gap-1',
          className,
        )}
        title="Updates pending - will apply when you close the current editor"
      >
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-500" />
        </span>
      </div>
    </td>
  )
})
